import { Exclude, Type } from 'class-transformer';
import { DateHelper } from '../helpers';

export enum TDateRangeOperator {
	Before = 1,     // Before the selected date
	BeforeOrOn = 2,     // Before or on the selected date
	After = 3,     // After the selected date
	AfterOrOn = 4,     // After or on the selected date
	Between = 5,     // Between the selected dates
	NotBetween = 6,     // Not between the selected dates
	On = 7,     // On the selected date
}

export enum TDateRangeFilterDataType {
	ActivationDate = 1,    // Activation Date
	ApplicationDate = 2,    // Application Date
	CancellationDate = 3,    // Cancellation Date
	PeriodEndDate = 4,    // Period End Date
	SuspensionDate = 5,    // Suspension Date
	PromoDiscountStartDate = 6,    // Promo Discount Date
	PromoDiscountEndDate = 7,    // Promo Discount End Date
	PromoDiscountPublishedDate = 8,    // Promo Discount Published Date
	ServiceCreatedDate = 9,    // Service Created Date
	WorkOrderCreatedDate = 10,   // WorkOrder Created Date
}

export function filterDataTypeAsText(filterDataType: TDateRangeFilterDataType): string {
	const filterMap: { [key in TDateRangeFilterDataType]: string } = {
		[TDateRangeFilterDataType.ActivationDate]: 'Activation Date',
		[TDateRangeFilterDataType.ApplicationDate]: 'Application Date',
		[TDateRangeFilterDataType.CancellationDate]: 'Cancellation Date',
		[TDateRangeFilterDataType.PeriodEndDate]: 'Period End Date',
		[TDateRangeFilterDataType.SuspensionDate]: 'Suspension Date',
		[TDateRangeFilterDataType.PromoDiscountStartDate]: 'Start Date',
		[TDateRangeFilterDataType.PromoDiscountEndDate]: 'End Date',
		[TDateRangeFilterDataType.PromoDiscountPublishedDate]: 'Published Date',
		[TDateRangeFilterDataType.ServiceCreatedDate]: 'Service Created Date',
		[TDateRangeFilterDataType.WorkOrderCreatedDate]: 'Work Order Created Date',
	};

	return filterMap[filterDataType] || ''; // Default to empty string if not found
}

export function dateRangeOperatorAsText(dateRangeOperator: TDateRangeOperator): string {
	switch (dateRangeOperator) {
		case TDateRangeOperator.Before:
			return 'Before';
		case TDateRangeOperator.BeforeOrOn:
			return 'Before or on';
		case TDateRangeOperator.After:
			return 'After';
		case TDateRangeOperator.AfterOrOn:
			return 'After or on';
		case TDateRangeOperator.Between:
			return 'Between';
		case TDateRangeOperator.NotBetween:
			return 'Not Between';
		case TDateRangeOperator.On:
			return 'On';
		default:
			return '';
	}
}

export interface IDateRangeSelector {
	selected: boolean;
	filter_data_type: TDateRangeFilterDataType;
	date_range_operator: TDateRangeOperator;
	date_range_operator_id: number;
	date_start_value?: Date | null;
	date_end_value?: Date | null;
	date_start: string;
	date_end: string;
	display_filter_data_type: string;
	display_date_range_operator: string;
}

export abstract class DateRangeSelectorItem implements IDateRangeSelector {
	public selected: boolean = false;
	public date_range_operator_id = 5;

	@Exclude()
	public get filter_data_type(): TDateRangeFilterDataType {
		return this.getFilterDataType();
	}

	public date_range_operator: TDateRangeOperator = TDateRangeOperator.After;
	@Type(() => Date)
	private _date_start_value?: Date | null;
	public get date_start_value(): Date | null {
		return this._date_start_value ?? null;
	}
	public set date_start_value(value: Date | null) {
		this._date_start_value = value;
		this.date_start = value ? DateHelper.formatDate(value) : '';
	}
	@Type(() => Date)
	private _date_end_value?: Date | null;
	public get date_end_value(): Date | null {
		return this._date_end_value ?? null;
	}
	public set date_end_value(value: Date | null) {
		this._date_end_value = value;
		this.date_end = value ? DateHelper.formatDate(value) : '';
	}
	public date_start: string;
	public date_end: string;

	@Exclude()
	public get display_filter_data_type(): string {
		return filterDataTypeAsText(this.filter_data_type);
	}

	@Exclude()
	public display_date_range_operator: string;

	protected abstract getFilterDataType(): TDateRangeFilterDataType;

	public clearFilter(): void {
		this.date_start_value = null;
		this.date_end_value = null;
		this.selected = false;
	}
}

export class ActivationDateRangeSelectorItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.ActivationDate;
	}
}

export class ApplicationDateRangeSelectorItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.ApplicationDate;
	}
}

export class CancellationDateRangeSelectorItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.CancellationDate;
	}
}

export class PeriodEndDateRangeSelectorItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.PeriodEndDate;
	}
}

export class SuspensionDateRangeSelectorItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.SuspensionDate;
	}
}

export class PromoDiscountStartDateRangeItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.PromoDiscountStartDate;
	}
}

export class PromoDiscountEndDateRangeItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.PromoDiscountEndDate;
	}
}

export class PromoDiscountPublishedDateRangeItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.PromoDiscountPublishedDate;
	}
}

export class ServiceCreatedDateRangeItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.ServiceCreatedDate;
	}
}

export class WorkOrderCreatedDateRangeItem extends DateRangeSelectorItem {
	protected getFilterDataType(): TDateRangeFilterDataType {
		return TDateRangeFilterDataType.WorkOrderCreatedDate;
	}
}
